import {
  ProcessedCategoryOption,
  ProcessedFilterOption,
} from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import {
  Box,
  Button,
  ChevronDownIcon,
  ContextHorizontalIcon,
  constants,
  Dropdown,
  DropdownItem,
  FilterIcon,
  Text,
  TickIcon,
} from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { DeviceType } from '@redbubble/next/types';
import {
  AnalyticsContextTypes,
  AnalyticsStorage,
  AnalyticsStorageKeys,
} from '@redbubble/next-analytics/gtm';
import { useNavigation } from '@redbubble/next-provider';
import { truncate } from 'lodash';
import { useEffect, useState } from 'react';
import styles from './FilterBar.module.scss';

export const messages = defineMessages({
  filters: {
    defaultMessage: `Filters{appliedCount, plural,
          =0 {}
          other { ({appliedCount})}}`,
  },
  allProducts: {
    defaultMessage: 'All Products',
  },
  appliedCategory: {
    defaultMessage: '{appliedCategory}',
  },
  totalResults: {
    defaultMessage: `{totalResults, plural,
    =0 {# items} 
    one {# item}
    other {# items}
    }`,
  },
  cappedResults: {
    defaultMessage: '1000+ items',
  },
});

interface FilterBarProps {
  categoryOptions?: ProcessedCategoryOption[];
  sortOptions?: ProcessedFilterOption[];
  onClick: (product?: boolean) => void;
  appliedCount?: number;
  totalResults?: number | null;
}
export const FilterBar: React.FC<FilterBarProps> = ({
  categoryOptions,
  sortOptions,
  onClick,
  appliedCount,
  totalResults,
}) => {
  const intl = useIntl();
  const { navigate } = useNavigation();
  const [appliedOption, setAppliedOption] = useState(
    sortOptions?.find((option) => option.applied)
  );
  const { isOrAbove } = useViewportWidth();

  useEffect(() => {
    setAppliedOption(sortOptions?.find((option) => option.applied));
  }, [sortOptions]);

  const isMobile = !isOrAbove(DeviceType.Tablet);

  const categoryLabel = categoryOptions?.find(
    (option) => option.applied
  )?.label;

  const appliedCategory = isMobile
    ? truncate(categoryLabel, { length: 18 })
    : categoryLabel;

  const categoryFilter = appliedCategory
    ? intl.formatMessage(messages.appliedCategory, {
        appliedCategory,
      })
    : intl.formatMessage(messages.allProducts);

  const handleClick = (option) => {
    setAppliedOption(option);
    navigate({ url: option.url });
  };

  return (
    <Box className={styles.filterBarContainer}>
      {totalResults && (
        <Box className={styles.resultCountMobile}>
          <Text muted type="body">
            {totalResults > 999
              ? intl.formatMessage(messages.cappedResults)
              : intl.formatMessage(messages.totalResults, { totalResults })}
          </Text>
        </Box>
      )}
      <Box className={styles.mobile}>
        <Box marginRight="s">
          <Button
            onClick={() => onClick(true)}
            strong
            iconBefore={<FilterIcon size={constants.SMALL} />}
          >
            {categoryFilter}
          </Button>
        </Box>
        <Button
          onClick={() => onClick()}
          iconBefore={<ContextHorizontalIcon size={constants.SMALL} />}
        >
          {intl.formatMessage(messages.filters, {
            appliedCount,
          })}
        </Button>
        {totalResults && (
          <Box className={styles.resultCount}>
            <Text muted type="body">
              {totalResults > 999
                ? intl.formatMessage(messages.cappedResults)
                : intl.formatMessage(messages.totalResults, { totalResults })}
            </Text>
          </Box>
        )}
      </Box>
      {sortOptions && (
        <Box className={styles.sort}>
          <Dropdown
            placement={constants.BOTTOM_RIGHT}
            trigger={(props, ref) => (
              <Button
                iconAfter={<ChevronDownIcon size={constants.SMALL} />}
                ref={ref}
                strong
                {...props}
              >
                {appliedOption?.label}
              </Button>
            )}
          >
            {sortOptions.map((option) => {
              return (
                <DropdownItem
                  key={option.name}
                  onSelect={() => {
                    AnalyticsStorage.setValue(
                      AnalyticsStorageKeys.RefContextType,
                      AnalyticsContextTypes.SortOrder
                    );
                    handleClick(option);
                  }}
                  iconAfter={option.applied && <TickIcon />}
                >
                  {option.label}
                </DropdownItem>
              );
            })}
          </Dropdown>
        </Box>
      )}
    </Box>
  );
};
